import React, { useEffect, useState, useRef } from "react";
import { X, FileText, Download } from "lucide-react";
import api from "../lib/api";

function scoreColor(v) {
  if (v >= 75) return "text-[#10B981]";
  if (v >= 50) return "text-[#F59E0B]";
  return "text-[#E11D48]";
}

function downloadResumeText(name, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `resume_${(name || "candidate").replace(/\s+/g, "_").toLowerCase()}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ResumePanel({ candidate, roleTitle, onClose }) {
  const [resumeText, setResumeText] = useState(null);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!candidate) return;
    setLoading(true);
    setResumeText(null);
    api.get(`/company/candidates/${candidate.candidate_id}/resume`)
      .then(({ data }) => setResumeText(data.resume_text || ""))
      .catch(() => setResumeText(""))
      .finally(() => setLoading(false));
  }, [candidate]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Trap focus and prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!candidate) return null;

  const hasText = resumeText && resumeText.trim();
  const isWiped = candidate.consent_status === "consent_expired";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 h-full w-full max-w-xl bg-white border-l border-border z-50 flex flex-col shadow-xl"
        role="dialog"
        aria-label="Resume viewer"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{candidate.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground truncate">{candidate.email}</div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Score + role strip */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {roleTitle && (
              <span className="font-mono text-[9px] px-2 py-0.5 bg-secondary border border-border text-muted-foreground">
                {roleTitle}
              </span>
            )}
            {candidate.match_score != null && (
              <span className={`font-mono text-[10px] font-semibold ${scoreColor(candidate.match_score)}`}>
                {Math.round(candidate.match_score)}% match
              </span>
            )}
            {candidate.interview_score != null && (
              <span className={`font-mono text-[10px] font-semibold ${scoreColor(candidate.interview_score)}`}>
                {Math.round(candidate.interview_score)}% interview
              </span>
            )}
            {hasText && (
              <button
                onClick={() => downloadResumeText(candidate.name, resumeText)}
                className="ml-auto flex items-center gap-1 font-mono text-[9px] tracking-[0.1em] text-muted-foreground hover:text-foreground transition-colors"
                title="Download as .txt"
              >
                <Download className="w-3 h-3" /> TXT
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="font-mono text-xs text-muted-foreground">Loading resume…</div>
          ) : isWiped ? (
            <div className="border border-[#E11D48]/20 bg-[#E11D48]/5 p-5 text-center">
              <div className="font-mono text-[10px] tracking-[0.15em] text-[#E11D48] mb-1">RESUME WIPED</div>
              <p className="text-sm text-muted-foreground">
                This candidate's PII including their resume was deleted when their consent expired.
              </p>
            </div>
          ) : !hasText ? (
            <div className="border border-border bg-[#FAFAFA] p-5 text-center">
              <div className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground mb-1">NO RESUME ON FILE</div>
              <p className="text-sm text-muted-foreground">This candidate was added without a resume upload.</p>
            </div>
          ) : (
            <pre className="font-mono text-[11px] leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {resumeText}
            </pre>
          )}
        </div>
      </div>
    </>
  );
}
